import React, { useEffect, useState } from 'react';
import { CloudSun, AlertTriangle, ShieldAlert } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { api } from '@/lib/api';
import { MarkdownText } from '@/components/MarkdownText';

const LEVELS = {
  none: { bar: null, bg: 'bg-white border-gray-100', text: 'text-[#2A8F8A]', Icon: CloudSun },
  green: { bar: 'bg-emerald-500', bg: 'bg-emerald-50 border-emerald-100', text: 'text-emerald-800', Icon: CloudSun },
  amber: { bar: 'bg-[#C4620C]', bg: 'bg-[#FBF1DF] border-[#C4620C]/20', text: 'text-[#7A3E08]', Icon: AlertTriangle },
  red: { bar: 'bg-[#6B3FA0]', bg: 'bg-[#EFE6F7] border-[#6B3FA0]/20', text: 'text-[#3E2466]', Icon: ShieldAlert },
};
const STATE_COLORS = { verde: '#16854A', naranja: '#C4620C', rojo: '#C82E28' };
const ZONE_ORDER = ['norte', 'medianias_norte', 'anaga_montana', 'laguna_esperanza', 'santa_cruz_costa', 'este_guimar', 'medianias_sur', 'sur_costa', 'oeste_teno', 'teide_cumbre'];
const ZONE_NAMES = {
  norte: { es: 'Norte', en: 'North', de: 'Norden' },
  medianias_norte: { es: 'Medianías norte', en: 'North midlands', de: 'Mittlere Höhen Nord' },
  anaga_montana: { es: 'Anaga montaña', en: 'Anaga mountains', de: 'Anaga-Gebirge' },
  laguna_esperanza: { es: 'La Laguna – La Esperanza', en: 'La Laguna – La Esperanza', de: 'La Laguna – La Esperanza' },
  santa_cruz_costa: { es: 'Santa Cruz costa', en: 'Santa Cruz coast', de: 'Santa Cruz Küste' },
  este_guimar: { es: 'Este – Güímar', en: 'East – Güímar', de: 'Osten – Güímar' },
  medianias_sur: { es: 'Medianías sur', en: 'South midlands', de: 'Mittlere Höhen Süd' },
  sur_costa: { es: 'Sur costa', en: 'South coast', de: 'Südküste' },
  oeste_teno: { es: 'Oeste – Teno', en: 'West – Teno', de: 'Westen – Teno' },
  teide_cumbre: { es: 'Teide cumbre', en: 'Teide summit', de: 'Teide-Gipfel' },
};
const zoneName = (id, lang) => ZONE_NAMES[id]?.[lang] || ZONE_NAMES[id]?.es || id;
const STATE_WORD = { es: { verde: 'bien', naranja: 'regular', rojo: 'mejor otra zona' }, en: { verde: 'good', naranja: 'fair', rojo: 'better elsewhere' }, de: { verde: 'gut', naranja: 'mäßig', rojo: 'anderswo besser' } };
const UPDATED = { es: 'Actualizado a las', en: 'Updated at', de: 'Aktualisiert um' };
const canaryTime = (iso) => { try { return new Intl.DateTimeFormat('es-ES', { timeZone: 'Atlantic/Canary', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso)); } catch { return null; } };

const TITLE = { es: 'El tiempo hoy en Tenerife', en: "Today's weather in Tenerife", de: 'Das Wetter heute auf Teneriffa' };

const formatDate = (iso, lang) => {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString(lang === 'en' ? 'en-GB' : lang === 'de' ? 'de-DE' : 'es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
  } catch { return iso; }
};

export const WeatherBriefingCard = ({ className = '' }) => {
  const { language } = useLanguage();
  const [briefing, setBriefing] = useState(null);

  useEffect(() => {
    api.getTodayBriefing().then(r => setBriefing(r.data.briefing)).catch(() => {});
  }, []);

  if (!briefing) return null;
  const lvl = LEVELS[briefing.alert_level] || LEVELS.none;
  const summary = briefing[`summary_${language}`] || briefing.summary_es;
  const notes = (briefing.zone_notes || []).filter(n => n[`note_${language}`] || n.note_es)
    .sort((a, b) => (ZONE_ORDER.indexOf(a.zone_id) === -1 ? 99 : ZONE_ORDER.indexOf(a.zone_id)) - (ZONE_ORDER.indexOf(b.zone_id) === -1 ? 99 : ZONE_ORDER.indexOf(b.zone_id)));
  const updated = canaryTime(briefing.updated_at || briefing.created_at);

  return (
    <div className={`relative overflow-hidden rounded-2xl border ${lvl.bg} ${className}`} data-testid="weather-briefing-card">
      {lvl.bar && <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${lvl.bar}`} data-testid="weather-briefing-alert-bar" />}
      <div className="pl-5 pr-4 py-4">
        <div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${lvl.text}`}>
          <lvl.Icon size={16} />
          <span>{TITLE[language] || TITLE.es}</span>
        </div>
        <p className="text-xs text-gray-500 mt-0.5"><span>{(() => { const d = formatDate(briefing.date, language); return d.charAt(0).toUpperCase() + d.slice(1); })()}</span>{updated && <span data-testid="weather-briefing-updated"> · {UPDATED[language] || UPDATED.es} {updated}</span>}</p>
        <MarkdownText text={summary} className="mt-3 text-[15px] text-gray-800 leading-relaxed" />
        {notes.length > 0 && (
          <div className="mt-4 grid gap-2 sm:grid-cols-2" data-testid="weather-briefing-zones">
            {notes.map(n => (
              <div key={n.zone_id} className="rounded-xl bg-white/70 px-3 py-2 text-sm text-gray-700" data-testid={`weather-briefing-zone-${n.zone_id}`} data-state={n.estado || ''}>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1 flex items-center gap-1.5">
                  {n.estado && STATE_COLORS[n.estado] && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: STATE_COLORS[n.estado] }} aria-label={STATE_WORD[language]?.[n.estado] || n.estado} />}
                  {zoneName(n.zone_id, language)}
                </p>
                <MarkdownText text={n[`note_${language}`] || n.note_es} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WeatherBriefingCard;
